import {
  CAlert,
  CButton,
  CModal,
  CModalBody,
  CModalHeader,
} from "@coreui/react";
import { DatasetScoreWarning, ScoringProgressBar } from "./DatasetComponents";
import {
  useGetDataset,
  useScoreDataset,
  useUnscoredQueue,
} from "./services/DatasetServices";

import { Can } from "src/helpers/Auth";
import PropTypes from "prop-types";
import React, { useState } from "react";
import ReactQueryLoading from "src/reusable/ReactQueryLoading";
import { useGetModelVersionByVersionId } from "../models/ModelService";
import { ScoreDatasetAdvancedOptions } from "src/reusable/components/ScoreDatasetAdvancedOptions";

export const ScoreDatasetModal = (props) => {
  const { data: dataset } = useGetDataset(props.datasetId);
  const { data: model } = useGetModelVersionByVersionId(props.modelVersionId);
  const [onlyNew, setOnlyNew] = useState(false);
  const [limitScoredRecords, setLimitScoredRecords] = useState("");
  const {
    mutate: scoreDataset,
    isLoading: isScoreDatasetLoading,
    status: scoreDatasetStatus,
    error: scoreDatasetError,
    reset: resetScoreDataset,
  } = useScoreDataset();

  const onClose = () => {
    props.onClose();
    resetScoreDataset();
  };

  const { data: unscoredDataStats } = useUnscoredQueue(
    props.datasetId,
    props.modelVersionId,
    scoreDatasetStatus !== "error",
  );

  return (
    <CModal show={props.isVisibile} onClose={() => onClose()}>
      <Can
        perform="dataset_data:read"
        yes={() => (
          <>
            {!!dataset && !!model && (
              <>
                <CModalHeader>
                  <h5>
                    Score Dataset <strong>{dataset.name}</strong> <br /> on
                    model <strong>{model.name}</strong>
                  </h5>
                </CModalHeader>
                <CModalBody>
                  {scoreDatasetStatus === "idle" ? (
                    <>
                      <CButton
                        color="primary"
                        data-attr="datasets:btn:score-dataset"
                        onClick={() =>
                          scoreDataset({
                            datasetId: props.datasetId,
                            modelVersionId: props.modelVersionId,
                            onlyNew: onlyNew,
                            limitScoredRecords:
                              limitScoredRecords === ""
                                ? null
                                : parseInt(limitScoredRecords, 10),
                          })
                        }
                      >
                        Score Dataset
                      </CButton>
                      <ScoreDatasetAdvancedOptions
                        setOnlyNew={setOnlyNew}
                        setLimitScoredRecords={setLimitScoredRecords}
                        limitScoredRecords={limitScoredRecords}
                        onlyNew={onlyNew}
                        isModal={true}
                      />
                    </>
                  ) : (
                    <>
                      <div style={{ minHeight: "40px" }}>
                        <ReactQueryLoading
                          isLoading={
                            (isScoreDatasetLoading ||
                              unscoredDataStats?.waiting > 0) &&
                            scoreDatasetStatus !== "error"
                          }
                          status={scoreDatasetStatus}
                          error={
                            scoreDatasetStatus === "error"
                              ? scoreDatasetError?.response?.data?.detail ||
                                scoreDatasetError?.message ||
                                "Unknown error"
                              : scoreDatasetError
                          }
                          loadingHeight="30px"
                        >
                          {!isScoreDatasetLoading &&
                            unscoredDataStats?.waiting === 0 &&
                            scoreDatasetStatus === "success" && (
                              <>
                                <CAlert color="primary">
                                  Data scoring completed.
                                </CAlert>
                                <DatasetScoreWarning
                                  datasetId={props.datasetId}
                                  modelVersionId={props.modelVersionId}
                                  additionalWarning="There are still unscored or old scored records in the dataset. This can happen if changes were made to the dataset during scoring, or if multiple attempts to score the dataset were made.  Wait for other scoring to complete or score the dataset again if the latest predictions are required."
                                />
                              </>
                            )}
                        </ReactQueryLoading>
                      </div>
                      <div style={{ textAlign: "center", marginTop: "10px" }}>
                        {isScoreDatasetLoading ||
                          (unscoredDataStats?.waiting > 0 &&
                            scoreDatasetStatus !== "error" && (
                              <ScoringProgressBar
                                unscoredDataStats={unscoredDataStats}
                              />
                            ))}
                      </div>
                    </>
                  )}
                </CModalBody>
              </>
            )}
          </>
        )}
        no={() => <div>You do not have permission to use this dataset</div>}
      />
    </CModal>
  );
};

ScoreDatasetModal.propTypes = {
  datasetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  modelVersionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isVisibile: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
